import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Separator } from './ui/separator';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import { Loader2, Check, Wifi, Copy, Plus, Trash2, ChevronDown, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import TimeInput from './TimeInput';

const EditableStreamingTab = ({ event, onUpdate }) => {
  const [streaming, setStreaming] = useState(event?.streaming || {});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [expandedStreams, setExpandedStreams] = useState({});
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, index: null });

  const pendingChanges = useRef({});
  const backupTimeoutRef = useRef(null);
  const isInitialMount = useRef(true);

  useEffect(() => {
    if (isInitialMount.current) {
      setStreaming(event?.streaming || {});
      isInitialMount.current = false;
    }
  }, [event?.streaming]);

  useEffect(() => {
    return () => {
      if (backupTimeoutRef.current) {
        clearTimeout(backupTimeoutRef.current);
      }
    };
  }, []);

  const doSave = useCallback(async () => {
    if (Object.keys(pendingChanges.current).length === 0) return;
    setSaving(true);
    const changesToSave = { ...pendingChanges.current };
    pendingChanges.current = {};
    try {
      await onUpdate({ streaming: changesToSave });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (error) {
      console.error('Failed to save:', error);
      pendingChanges.current = { ...changesToSave, ...pendingChanges.current };
    } finally {
      setSaving(false);
    }
  }, [onUpdate]);

  const queueChange = useCallback((newStreaming) => {
    pendingChanges.current = newStreaming;
    if (backupTimeoutRef.current) clearTimeout(backupTimeoutRef.current);
    backupTimeoutRef.current = setTimeout(() => doSave(), 1000);
  }, [doSave]);

  const saveNow = useCallback(() => {
    if (backupTimeoutRef.current) clearTimeout(backupTimeoutRef.current);
    doSave();
  }, [doSave]);

  const handleFieldChange = (field, value) => {
    const newStreaming = { ...streaming, [field]: value };
    setStreaming(newStreaming);
    queueChange(newStreaming);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  // Stream entries management
  const addStream = () => {
    const newStream = {
      id: `stream-${Date.now()}`,
      description: '',
      rtmp_url: '',
      stream_key: '',
      backup_url: '',
      audience_link: '',
      embed_code: '',
      start_time: '',
      end_time: '',
      notes: '',
    };
    const newStreams = [...(streaming.streams || []), newStream];
    const newStreaming = { ...streaming, streams: newStreams };
    setStreaming(newStreaming);
    queueChange(newStreaming);
    setExpandedStreams(prev => ({ ...prev, [newStream.id]: true }));
  };

  const updateStream = (index, field, value) => {
    const newStreams = [...(streaming.streams || [])];
    newStreams[index] = { ...newStreams[index], [field]: value };
    const newStreaming = { ...streaming, streams: newStreams };
    setStreaming(newStreaming);
    queueChange(newStreaming);
  };

  const removeStream = () => {
    if (deleteConfirm.index === null) return;
    const newStreams = (streaming.streams || []).filter((_, i) => i !== deleteConfirm.index);
    const newStreaming = { ...streaming, streams: newStreams };
    setStreaming(newStreaming);
    queueChange(newStreaming);
    setDeleteConfirm({ open: false, index: null });
    toast.success('Stream removed');
    setTimeout(saveNow, 0);
  };

  const toggleStreamExpanded = (streamId) => {
    setExpandedStreams(prev => ({ ...prev, [streamId]: !prev[streamId] }));
  };

  const formatTime12 = (t) => {
    if (!t) return '';
    const [h, m] = t.split(':').map(Number);
    if (isNaN(h)) return t;
    const suffix = h >= 12 ? 'PM' : 'AM';
    const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
    return `${h12}:${String(m).padStart(2, '0')} ${suffix}`;
  };

  return (
    <Card className="border-border/50 bg-card/50">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg font-semibold flex items-center gap-2">
            <Wifi className="h-5 w-5 text-green-400" />
            Streaming
          </CardTitle>
          <div className="flex items-center h-6">
            {saving && (
              <Badge variant="outline" className="border-blue-500/30 bg-blue-500/10 text-blue-400">
                <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                Saving...
              </Badge>
            )}
            {saved && !saving && (
              <Badge variant="outline" className="border-emerald-500/30 bg-emerald-500/10 text-emerald-400">
                <Check className="h-3 w-3 mr-1" />
                Saved
              </Badge>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Overview Fields */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="text-sm text-muted-foreground uppercase tracking-wider mb-2 block">What?</Label>
            <Textarea
              value={streaming.what || ''}
              onChange={(e) => handleFieldChange('what', e.target.value)}
              onBlur={saveNow}
              placeholder="What are we streaming?"
              rows={3}
              className="bg-background/50"
            />
          </div>
          <div>
            <Label className="text-sm text-muted-foreground uppercase tracking-wider mb-2 block">Broadcast Layout</Label>
            <Textarea
              value={streaming.broadcast_layout || ''}
              onChange={(e) => handleFieldChange('broadcast_layout', e.target.value)}
              onBlur={saveNow}
              placeholder="Describe the broadcast layout..."
              rows={3}
              className="bg-background/50"
            />
          </div>
          <div>
            <Label className="text-sm text-muted-foreground uppercase tracking-wider mb-2 block">Remote Calls</Label>
            <Textarea
              value={streaming.remote_calls || ''}
              onChange={(e) => handleFieldChange('remote_calls', e.target.value)}
              onBlur={saveNow}
              placeholder="Remote call requirements..."
              rows={3}
              className="bg-background/50"
            />
          </div>
          <div>
            <Label className="text-sm text-muted-foreground uppercase tracking-wider mb-2 block">Lower Thirds / Captioning</Label>
            <Textarea
              value={streaming.lower_thirds_captioning || ''}
              onChange={(e) => handleFieldChange('lower_thirds_captioning', e.target.value)}
              onBlur={saveNow}
              placeholder="Lower thirds and captioning info..."
              rows={3}
              className="bg-background/50"
            />
          </div>
        </div>

        <Separator />

        {/* Stream Entries */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <Label className="text-sm text-muted-foreground uppercase tracking-wider">Streams</Label>
            <Button type="button" variant="outline" size="sm" onClick={addStream}>
              <Plus className="h-4 w-4 mr-1" /> Add Stream
            </Button>
          </div>

          {(streaming.streams || []).length === 0 ? (
            <p className="text-muted-foreground text-center py-4 text-sm">No streams configured. Click "Add Stream" to add one.</p>
          ) : (
            <div className="space-y-3">
              {(streaming.streams || []).map((stream, idx) => {
                const isExpanded = expandedStreams[stream.id] !== false;
                const timeRange = (() => {
                  const parts = [];
                  if (stream.start_date || stream.start_time) {
                    const d = stream.start_date ? format(parseISO(stream.start_date), 'MMM d') : '';
                    const t = formatTime12(stream.start_time);
                    parts.push([d, t].filter(Boolean).join(' '));
                  }
                  if (stream.end_date || stream.end_time) {
                    const d = stream.end_date ? format(parseISO(stream.end_date), 'MMM d') : '';
                    const t = formatTime12(stream.end_time);
                    parts.push([d, t].filter(Boolean).join(' '));
                  }
                  return parts.length > 0 ? parts.join(' - ') : '';
                })();

                return (
                  <div key={stream.id || idx} className="border border-border/50 rounded-lg overflow-hidden bg-background/30">
                    {/* Stream Header */}
                    <div
                      className="flex items-center gap-3 p-3 cursor-pointer hover:bg-muted/30 transition-colors"
                      onClick={() => toggleStreamExpanded(stream.id)}
                    >
                      {isExpanded ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
                      <Wifi className="h-4 w-4 text-green-400" />
                      <span className="font-medium text-sm">{stream.description || `Stream ${idx + 1}`}</span>
                      {timeRange && <span className="text-xs text-muted-foreground">{timeRange}</span>}
                      {stream.rtmp_url && <Badge variant="outline" className="text-xs border-green-500/30 bg-green-500/10 text-green-400 ml-auto">RTMP</Badge>}
                      {stream.backup_url && <Badge variant="outline" className="text-xs border-amber-500/30 bg-amber-500/10 text-amber-400">Backup</Badge>}
                    </div>

                    {isExpanded && (
                      <div className="border-t border-border/50 p-3 space-y-4">
                        {/* Row 1: Description */}
                        <div>
                          <Label className="text-xs text-muted-foreground">Description</Label>
                          <Input
                            value={stream.description || ''}
                            onChange={(e) => updateStream(idx, 'description', e.target.value)}
                            onBlur={saveNow}
                            placeholder="Main stage keynote stream"
                            className="bg-background/50 h-8 text-sm"
                          />
                        </div>

                        {/* Row 2: Start/End Date+Time */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                          <div>
                            <Label className="text-xs text-muted-foreground">Start Date</Label>
                            <Input
                              type="date"
                              value={stream.start_date || ''}
                              onChange={(e) => updateStream(idx, 'start_date', e.target.value)}
                              onBlur={saveNow}
                              className="bg-background/50 h-8 text-xs"
                            />
                          </div>
                          <div>
                            <Label className="text-xs text-muted-foreground">Start Time</Label>
                            <TimeInput
                              value={stream.start_time || ''}
                              onChange={(val) => updateStream(idx, 'start_time', val)}
                              className="bg-background/50 h-8 text-xs px-2 rounded-md border border-input"
                            />
                          </div>
                          <div>
                            <Label className="text-xs text-muted-foreground">End Date</Label>
                            <Input
                              type="date"
                              value={stream.end_date || ''}
                              onChange={(e) => updateStream(idx, 'end_date', e.target.value)}
                              onBlur={saveNow}
                              className="bg-background/50 h-8 text-xs"
                            />
                          </div>
                          <div>
                            <Label className="text-xs text-muted-foreground">End Time</Label>
                            <TimeInput
                              value={stream.end_time || ''}
                              onChange={(val) => updateStream(idx, 'end_time', val)}
                              className="bg-background/50 h-8 text-xs px-2 rounded-md border border-input"
                            />
                          </div>
                        </div>

                        {/* Row 2: RTMP + Stream Key */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <div>
                            <Label className="text-xs text-muted-foreground">RTMP URL</Label>
                            <div className="flex items-center gap-1">
                              <Input
                                value={stream.rtmp_url || ''}
                                onChange={(e) => updateStream(idx, 'rtmp_url', e.target.value)}
                                onBlur={saveNow}
                                placeholder="rtmp://..."
                                autoComplete="off"
                                data-1p-ignore
                                className="bg-background/50 h-8 text-sm font-mono"
                              />
                              {stream.rtmp_url && (
                                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => copyToClipboard(stream.rtmp_url)}>
                                  <Copy className="h-3.5 w-3.5" />
                                </Button>
                              )}
                            </div>
                          </div>
                          <div>
                            <Label className="text-xs text-muted-foreground">Stream Key</Label>
                            <div className="flex items-center gap-1">
                              <Input
                                value={stream.stream_key || ''}
                                onChange={(e) => updateStream(idx, 'stream_key', e.target.value)}
                                onBlur={saveNow}
                                placeholder="Stream key..."
                                autoComplete="off"
                                data-1p-ignore
                                className="bg-background/50 h-8 text-sm font-mono"
                              />
                              {stream.stream_key && (
                                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => copyToClipboard(stream.stream_key)}>
                                  <Copy className="h-3.5 w-3.5" />
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Row 3: Backup + Audience Link */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <div>
                            <Label className="text-xs text-muted-foreground">Backup URL</Label>
                            <div className="flex items-center gap-1">
                              <Input
                                value={stream.backup_url || ''}
                                onChange={(e) => updateStream(idx, 'backup_url', e.target.value)}
                                onBlur={saveNow}
                                placeholder="rtmp://backup..."
                                autoComplete="off"
                                data-1p-ignore
                                className="bg-background/50 h-8 text-sm font-mono"
                              />
                              {stream.backup_url && (
                                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => copyToClipboard(stream.backup_url)}>
                                  <Copy className="h-3.5 w-3.5" />
                                </Button>
                              )}
                            </div>
                          </div>
                          <div>
                            <Label className="text-xs text-muted-foreground">Audience Link</Label>
                            <div className="flex items-center gap-1">
                              <Input
                                value={stream.audience_link || ''}
                                onChange={(e) => updateStream(idx, 'audience_link', e.target.value)}
                                onBlur={saveNow}
                                placeholder="https://..."
                                className="bg-background/50 h-8 text-sm"
                              />
                              {stream.audience_link && (
                                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => copyToClipboard(stream.audience_link)}>
                                  <Copy className="h-3.5 w-3.5" />
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Row 4: Embed Code */}
                        <div>
                          <Label className="text-xs text-muted-foreground">Embed Code</Label>
                          <div className="flex items-start gap-1">
                            <Textarea
                              value={stream.embed_code || ''}
                              onChange={(e) => updateStream(idx, 'embed_code', e.target.value)}
                              onBlur={saveNow}
                              placeholder="<iframe>...</iframe>"
                              rows={2}
                              className="bg-background/50 font-mono text-xs"
                            />
                            {stream.embed_code && (
                              <Button variant="ghost" size="icon" className="h-8 w-8 mt-1" onClick={() => copyToClipboard(stream.embed_code)}>
                                <Copy className="h-3.5 w-3.5" />
                              </Button>
                            )}
                          </div>
                        </div>

                        {/* Row 5: Notes */}
                        <div>
                          <Label className="text-xs text-muted-foreground">Notes</Label>
                          <Textarea
                            value={stream.notes || ''}
                            onChange={(e) => updateStream(idx, 'notes', e.target.value)}
                            onBlur={saveNow}
                            placeholder="Additional streaming notes..."
                            rows={2}
                            className="bg-background/50 text-sm"
                          />
                        </div>

                        {/* Delete button */}
                        <div className="flex justify-end">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setDeleteConfirm({ open: true, index: idx })}
                            className="text-destructive hover:text-destructive hover:bg-destructive/10"
                          >
                            <Trash2 className="h-4 w-4 mr-1" /> Remove Stream
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </CardContent>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteConfirm.open} onOpenChange={(open) => !open && setDeleteConfirm({ open: false, index: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Stream</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this stream? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={removeStream}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
};

export default EditableStreamingTab;
